// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-1 and Layout-5): the tabs of the My Work page.
//
// A real tablist: role=tablist / tab, aria-selected, aria-controls (the one tab panel), a roving tabindex (only the selected tab is in the Tab order), and the Left /
// Right / Home / End keys move to (and select) the neighbouring tab, wrapping at the ends. The row scrolls horizontally inside itself when the tabs do not fit, so no
// tab ever wraps out of sight or pushes the page wider. The selected tab has larger bold blue text and a 4px blue underline across its full width; its count sits beside the label (the count
// is also in the accessible name, scoped: "My Tasks, 4 open"). Which tabs exist, their labels and counts are pageTabsModel's.
import type { KeyboardEvent, ReactNode } from 'react';
import { tabKeyTarget } from './pageTabsModel';
import type { PageTab, PageTabKey } from './pageTabsModel';

export const TAB_PANEL_ID = 'work-panel';

export interface PageTabsProps {
  tabs: readonly PageTab[];
  selected: PageTabKey;
  onSelect: (key: PageTabKey) => void;
  /** One small control group at the right end of the tab row, just above the divider line (the Comfortable / Compact buttons). Absent or null draws nothing. */
  trailing?: ReactNode;
}

export function PageTabs({ tabs, selected, onSelect, trailing = null }: PageTabsProps) {
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const next = tabKeyTarget(event.key, index, tabs.length);
    if (next < 0) return;
    event.preventDefault();
    const target = tabs[next];
    onSelect(target.key);
    document.getElementById(target.id)?.focus();
  }
  return (
    <div className="mb-3 flex items-end gap-3 border-b border-gray-200">
      <div role="tablist" aria-label="My Work views" className="-mb-px flex min-w-0 gap-1 overflow-x-auto whitespace-nowrap" data-testid="page-tabs">
        {tabs.map((tab, index) => {
          const active = tab.key === selected;
          return (
            <button
              key={tab.key}
              id={tab.id}
              role="tab"
              type="button"
              aria-selected={active}
              aria-controls={TAB_PANEL_ID}
              aria-label={tab.accessibleName}
              title={tab.title}
              tabIndex={active ? 0 : -1}
              onClick={() => onSelect(tab.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={`inline-flex flex-shrink-0 items-center gap-2 border-b-4 px-5 py-2 leading-7 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2a7fb8] ${
                active ? 'border-[#1f6fa5] text-[#1f6fa5]' : 'border-transparent text-gray-700 hover:text-gray-900'
              }`}
            >
              {/* The invisible ::after copy is the selected (bold, larger) label: it reserves that width, so a tab never changes size when it is selected. */}
              <span
                data-label={tab.label}
                className={`inline-flex flex-col text-center after:invisible after:block after:h-0 after:select-none after:overflow-hidden after:text-lg after:font-bold after:content-[attr(data-label)] ${
                  active ? 'text-lg font-bold' : 'text-base font-medium'
                }`}
              >
                {tab.label}
              </span>
              {tab.count !== null && (
                <span
                  aria-hidden="true"
                  data-label={tab.count.text}
                  className={`inline-flex flex-col text-center after:invisible after:block after:h-0 after:select-none after:overflow-hidden after:text-base after:font-bold after:content-[attr(data-label)] ${
                    active ? 'text-base font-bold text-[#1f6fa5]' : 'text-sm font-normal text-gray-600'
                  }`}
                  data-testid={`tab-count-${tab.key}`}
                >
                  {tab.count.text}
                </span>
              )}
              {/* Team / Company before its count is known (it loads when the tab is opened): a dash, never a number and never a zero. The accessible name above says "count not loaded yet". */}
              {tab.count === null && tab.countPending === true && (
                <span aria-hidden="true" className={active ? 'text-base font-bold text-[#1f6fa5]' : 'text-sm font-normal text-gray-500'} data-testid={`tab-count-pending-${tab.key}`}>
                  {'\u2013'}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {trailing !== null && (
        <div className="ml-auto flex-shrink-0 pb-1.5" data-testid="tabs-trailing">
          {trailing}
        </div>
      )}
    </div>
  );
}
